import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeProvider";
import { useContext, useState, useEffect, useMemo } from 'react';
import { BundleContext } from '../../context/BundleProvider';
import {
    DIAGNOSTIC_TEST_QUESTION_COUNT,
    DIAGNOSTIC_TEST_SECONDS_PER_QUESTION,
} from "@learnzapp/core/readiness";
import { getExamDetails } from '../../utils/examDetails';
import OnboardingLayout from './OnboardingLayout';
import { useAnalytics } from '../../context/AnalyticsProvider';

const Exam = () => {
    const navigate = useNavigate();
    const { setSelectedBundle } = useContext(BundleContext);
    const { theme, currentTheme } = useContext(ThemeContext);
    const isDark = currentTheme === 'dark';
    const {
        trackOnboardingStep,
        trackExamSelection,
        trackDiagnosticStartClicked,
        trackDiagnosticSkipped,
    } = useAnalytics();
    const examDetails = useMemo(() => getExamDetails(), []);
    // Mirrors mobile: first exam pre-selected so the preview + CTA always show.
    const [selectedExam, setSelectedExam] = useState(examDetails[0] || null);
    const [loading, setLoading] = useState(false);

    const diagnosticMinutes = Math.round(
        (DIAGNOSTIC_TEST_QUESTION_COUNT * DIAGNOSTIC_TEST_SECONDS_PER_QUESTION) / 60
    );

    useEffect(() => {
        trackOnboardingStep('exam_selection', {
            step_number: 1
        });
    }, [trackOnboardingStep]);

    const styles = {
        container: {
            minHeight: '100vh',
        },
        content: {
            maxWidth: '1100px',
            margin: '0 auto',
            padding: '2rem 1rem 3rem 1rem',
            paddingTop: '5rem',
        },
        header: {
            textAlign: 'center',
            marginBottom: '2.5rem',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: '800',
            marginBottom: '0.75rem',
            color: theme.text.primary,
        },
        subtitle: {
            fontSize: '1.375rem',
            color: theme.text.secondary,
            fontWeight: '400',
        },
        chipGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
        },
        chip: (isSelected) => ({
            background: isSelected
                ? theme.primary?.blue?.[500]
                : theme.dashboard.card.background,
            border: `1px solid ${isSelected
                ? theme.primary?.blue?.[500]
                : theme.dashboard.card.border}`,
            borderRadius: '0.75rem',
            padding: '1.25rem 0.75rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            textAlign: 'center',
        }),
        chipVendor: (isSelected) => ({
            fontSize: '0.875rem',
            fontWeight: '600',
            marginBottom: '0.25rem',
            color: isSelected
                ? theme.neutral?.absolute_white
                : theme.primary?.blue?.[500],
            opacity: isSelected ? 0.85 : 1,
        }),
        chipName: (isSelected) => ({
            fontSize: '1.125rem',
            fontWeight: '700',
            color: isSelected
                ? theme.neutral?.absolute_white
                : theme.text.primary,
        }),
        detailPanel: {
            borderRadius: '0.75rem',
            border: `1px solid ${theme.dashboard.card.border}`,
            background: isDark
                ? theme.dashboard.card.background
                : theme.primary.blue[50],
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left',
        },
        detailHeader: {
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: `1px solid ${theme.dashboard.card.border}`,
        },
        detailTitle: {
            fontSize: '1.25rem',
            fontWeight: '700',
            color: theme.text.primary,
        },
        detailDescription: {
            fontSize: '0.9375rem',
            color: theme.text.secondary,
            marginTop: '0.25rem',
        },
        sectionLabel: {
            fontSize: '0.8125rem',
            fontWeight: '700',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: theme.text.secondary,
            marginBottom: '0.5rem',
        },
        sectionLabelSpaced: {
            marginTop: '1.25rem',
            paddingTop: '1.25rem',
            borderTop: `1px solid ${theme.dashboard.card.border}`,
        },
        detailLead: {
            fontSize: '1.0625rem',
            fontWeight: '500',
            color: theme.text.primary,
            marginTop: '0.5rem',
        },
        detailSub: {
            fontSize: '0.9375rem',
            color: theme.text.secondary,
            marginTop: '0.5rem',
            marginBottom: '0.25rem',
        },
        checkRow: {
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem 0',
        },
        checkIcon: {
            width: '1.75rem',
            color: theme.primary?.blue?.[500],
            fontWeight: '700',
            flexShrink: 0,
        },
        checkLabel: {
            fontSize: '1.0625rem',
            fontWeight: '500',
            color: theme.text.primary,
        },
        ctaContainer: {
            textAlign: 'center',
        },
        primaryButton: {
            width: '100%',
            border: 'none',
            borderRadius: '9999px',
            background: theme.electric?.blue || theme.primary?.blue?.[500],
            color: theme.neutral?.absolute_white,
            fontSize: '1.25rem',
            fontWeight: '800',
            padding: '1.25rem 1.5rem',
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
        },
        skipLink: {
            display: 'inline-block',
            marginTop: '1rem',
            background: 'none',
            border: 'none',
            fontSize: '0.9375rem',
            fontWeight: '500',
            color: theme.text.secondary,
            cursor: loading ? 'not-allowed' : 'pointer',
        },
        loadingOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `${theme.backgrounds.primary}80`,
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        },
        loadingContent: {
            background: theme.dashboard.card.background,
            padding: '2rem',
            borderRadius: '1rem',
            textAlign: 'center',
            border: `1px solid ${theme.dashboard.card.border}`,
        },
        loadingText: {
            color: theme.text.primary,
            fontSize: '1.125rem',
            fontWeight: '600',
            marginTop: '1rem',
        }
    };

    const handleSelect = async (exam) => {
        if (loading) return;
        setSelectedExam(exam);
        await trackExamSelection(exam.key, {
            selection_source: 'onboarding',
            exam_name: exam.name
        });
    };

    const handleStartAssessment = async () => {
        if (!selectedExam || loading) return;
        await trackDiagnosticStartClicked(selectedExam.key, {
            exam_name: selectedExam.name,
        });
        setLoading(true);
        try {
            await setSelectedBundle(selectedExam.key);
            setTimeout(() => {
                navigate('/onboarding-test', { state: { selectedExam: selectedExam } });
            }, 1000);
        } catch (error) {
            console.error('Error starting assessment:', error);
            setLoading(false);
        }
    };

    const handleSkip = () => {
        if (loading) return;
        trackDiagnosticSkipped({ exam: selectedExam?.key });
        navigate('/prepare');
    };

    const displayTitle = selectedExam?.vendor
        ? `${selectedExam.vendor} ${selectedExam.displayName || selectedExam.name}`
        : selectedExam?.name;

    const receiveRows = [
        'Readiness Score',
        'Weakest Domains',
        'Personalized Study Recommendations',
    ];

    const includedRows = selectedExam
        ? [
            { count: selectedExam.numQuestions, label: 'Practice Questions' },
            { count: selectedExam.numPracticeTests, label: 'Full Practice Exams' },
            { count: selectedExam.numFlashcards, label: 'Flashcards' },
            { count: selectedExam.numArticles, label: 'Study Articles' },
        ]
            .filter((row) => row.count > 0)
            .map((row) => `${row.count} ${row.label}`)
        : [];

    const renderCheckRow = (label) => (
        <div key={label} style={styles.checkRow}>
            <span style={styles.checkIcon}>✓</span>
            <span style={styles.checkLabel}>{label}</span>
        </div>
    );

    return (
        <OnboardingLayout>
            <div style={styles.container}>
                <div style={styles.content}>
                    {/* Header */}
                    <div style={styles.header}>
                        <h1 style={styles.title}>
                            Free Readiness Assessment
                        </h1>
                        <p style={styles.subtitle}>
                            Choose your certification
                        </p>
                    </div>

                    {/* Exam Chips */}
                    <div style={styles.chipGrid}>
                        {examDetails.map((exam) => {
                            const isSelected = selectedExam?.key === exam.key;
                            return (
                                <div
                                    key={exam.key}
                                    style={styles.chip(isSelected)}
                                    onClick={() => handleSelect(exam)}
                                >
                                    {!!exam.vendor && (
                                        <div style={styles.chipVendor(isSelected)}>
                                            {exam.vendor}
                                        </div>
                                    )}
                                    <div style={styles.chipName(isSelected)}>
                                        {exam.displayName || exam.name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Assessment Preview */}
                    {selectedExam && (
                        <div style={styles.detailPanel}>
                            <div style={styles.detailHeader}>
                                <div style={styles.detailTitle}>{displayTitle}</div>
                                <div style={styles.detailDescription}>
                                    {selectedExam.description}
                                </div>
                            </div>

                            <div style={styles.sectionLabel}>
                                Free Readiness Assessment
                            </div>
                            <div style={styles.detailLead}>
                                Answer {DIAGNOSTIC_TEST_QUESTION_COUNT} questions (~{diagnosticMinutes} minutes).
                            </div>
                            <div style={styles.detailSub}>
                                We'll analyze your performance and generate:
                            </div>
                            <div>{receiveRows.map(renderCheckRow)}</div>

                            {includedRows.length > 0 && (
                                <>
                                    <div style={{ ...styles.sectionLabel, ...styles.sectionLabelSpaced }}>
                                        Included for this Certification
                                    </div>
                                    <div>{includedRows.map(renderCheckRow)}</div>
                                </>
                            )}
                        </div>
                    )}

                    {/* CTA */}
                    <div style={styles.ctaContainer}>
                        <button
                            style={styles.primaryButton}
                            disabled={loading}
                            onClick={handleStartAssessment}
                        >
                            {loading
                                ? 'Preparing Assessment...'
                                : `Start ${selectedExam?.displayName || selectedExam?.name || ''} Assessment`}
                        </button>
                        <button
                            style={styles.skipLink}
                            disabled={loading}
                            onClick={handleSkip}
                        >
                            Skip for now
                        </button>
                    </div>
                </div>
            </div>
            {/* Loading Overlay */}
            {loading && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingContent}>
                        <div style={{
                            width: '3rem',
                            height: '3rem',
                            border: `3px solid ${theme.dashboard.card.border}`,
                            borderRadius: '50%',
                            margin: '0 auto',
                            background: theme.primary.blue[500],
                        }} />
                        <div style={styles.loadingText}>
                            Preparing your personalized assessment...
                        </div>
                    </div>
                </div>
            )}
        </OnboardingLayout>
    );
};

export default Exam;
